/*
<summary>
   This functional component is all about rendering the "Applicator Setting" popup, opened from
   an Applicator row's "Mounted Main Unit" column. It shows Close/Add when no Main Unit is
   mounted, or Close/Unmount/Swap (each with a Yes/No confirmation) when one already is.
   Developer: Aashish Singh, Created Date: 26-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import LoaderButton from '../../../shared-components/button/loader-button';
import ConfirmPopup from '../../../shared-components/popup/confirm/confirm-popup';
import { ApplicatorSettingValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormikFormSelect from '../../../shared-components/FormikFormHandlers/FormikFormSelect';
import Label from '../../../shared-components/label/label';
import { IOption } from '../../../models/ICommon';

interface IApplicatorSettingFormValues {
  SelectedId: number;
}

interface IProps {
  toolManagementNo: string;
  options: IOption[];
  currentSelectedId: number | null;
  currentSelectedLabel?: string | null;
  isLoading: boolean;
  modalClosed: () => void;
  modalSubmit: (selectedId: number) => void;
  modalUnmount: () => void;
}


const ApplicatorSettingPopup = (props: IProps) => {
  const [confirmAction, setConfirmAction] = useState<'unmount' | 'swap' | null>(null);
  const [pendingSelectedId, setPendingSelectedId] = useState<number | null>(null);

  const hasCurrentValue = !!props.currentSelectedId;

  const initialValues: IApplicatorSettingFormValues = {
    SelectedId: -1,
  };

  const options: IOption[] = [
    { id: -1, value: 'please_select', disabled: false },
    ...props.options,
  ];

  return (
    <>
      <Modal
        show={true}
        size='sm'
        centered
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={ApplicatorSettingValidateSchema}
          validateOnBlur={false}
          onSubmit={(values: IApplicatorSettingFormValues) =>
            props.modalSubmit(Number(values.SelectedId))
          }
        >
          {({ values, validateForm, setTouched }) => (
            <Form>
              <Modal.Header>
                <Modal.Title>
                  <FormattedMessage id="applicator_setting" /> - {props.toolManagementNo}
                  <button
                    onClick={props.modalClosed}
                    className="Crossicons"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="23"
                      height="23"
                      fill="currentColor"
                      className="bi bi-x"
                      viewBox="0 0 16 16"
                    >
                      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                    </svg>
                  </button>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {hasCurrentValue && (
                  <div className="mb-3">
                    <span className="fw-bold me-1">
                      <FormattedMessage id="currently_mounted" />:
                    </span>
                    <span>{props.currentSelectedLabel}</span>
                  </div>
                )}
                <div className="form-group mb-0">
                  <Label
                    className="label-style col-form-label"
                    required
                    label="select_main_unit"
                  />
                  <Field
                    name="SelectedId"
                    as={FormikFormSelect}
                    options={options}
                    disabled={props.isLoading}
                    className="form-control"
                  />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="light-danger"
                  onClick={props.modalClosed}
                  disabled={props.isLoading}
                >
                  <FormattedMessage id="cancel" />
                </Button>
                {hasCurrentValue ? (
                  <>
                    <LoaderButton
                      type="button"
                      isLoading={props.isLoading}
                      text="unmount"
                      onClick={() => setConfirmAction('unmount')}
                    />
                    <LoaderButton
                      type="button"
                      isLoading={props.isLoading}
                      text="swap"
                      onClick={async () => {
                        const errors = await validateForm();
                        if (Object.keys(errors).length > 0) {
                          setTouched({ SelectedId: true });
                          return;
                        }
                        setPendingSelectedId(Number(values.SelectedId));
                        setConfirmAction('swap');
                      }}
                    />
                  </>
                ) : (
                  <LoaderButton
                    type="submit"
                    isLoading={props.isLoading}
                    text="add"
                  />
                )}
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {confirmAction === 'unmount' && (
        <ConfirmPopup
          title="confirm_unmount_applicator"
          message="confirm_unmount_applicator_message"
          isLoading={props.isLoading}
          modalClosed={() => setConfirmAction(null)}
          modalSubmit={props.modalUnmount}
        />
      )}
      {confirmAction === 'swap' && (
        <ConfirmPopup
          title="confirm_swap_applicator"
          message="confirm_swap_applicator_message"
          isLoading={props.isLoading}
          modalClosed={() => setConfirmAction(null)}
          modalSubmit={() => {
            if (pendingSelectedId !== null) props.modalSubmit(pendingSelectedId);
          }}
        />
      )}
    </>
  );
};

export default ApplicatorSettingPopup;
